import React, { useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, Typography, Grid, Box, Chip, Paper } from '@mui/material';
import { useAtomValue } from 'jotai';
import { allSitesRanDb } from '../../common/atom/atoms';
import { useRanDbDashboard } from './context/ranDbDashboardContext';
import FancyLoader from '../../common/components/FancyLoader.component';
import * as echarts from 'echarts';

export default function RanDBOverview() {
  const data = useAtomValue(allSitesRanDb);
  const validData = Array?.isArray(data) ? data : [];
  const { dashboardData, loading, error, isInitialized, fetchDashboardData } = useRanDbDashboard();

  console.log('RanDB Overview - Dashboard data:', dashboardData);

  // Ensure dashboard data is loaded when overview component mounts
  useEffect(() => {
    if (!isInitialized && !loading) {
      console.log('RanDB Overview component mounted - ensuring dashboard data is loaded');
      fetchDashboardData();
    }
  }, []);

  // Chart refs
  const siteStatusChartRef = useRef(null);
  const vendorChartRef = useRef(null);
  const regionChartRef = useRef(null);
  const technologyChartRef = useRef(null);
  const coverageTypeChartRef = useRef(null);
  const siteTypeChartRef = useRef(null);
  const monthlyTrendsChartRef = useRef(null);
  const interactiveChartRef = useRef(null);

  // Calculate statistics using dashboard data
  const totalSites = dashboardData.length;

  // Helper function to get status from different possible field names
  const getStatus = (site) => {
    return site.site_status || site.status || site.current_status;
  };

  // Count sites by status
  const activeSites = dashboardData.filter((site) => getStatus(site) === 'Active').length;
  const inactiveSites = dashboardData.filter((site) => getStatus(site) === 'Inactive').length;
  const maintenanceSites = dashboardData.filter((site) => getStatus(site) === 'Maintenance').length;
  const nullStatusSites = dashboardData.filter(
    (site) => getStatus(site) === null || getStatus(site) === undefined || getStatus(site) === ''
  ).length;

  // Calculate vendor breakdown
  const vendorData = useMemo(() => {
    const vendors = {};
    dashboardData.forEach((site) => {
      const vendor = site.vendor || 'Unknown';
      vendors[vendor] = (vendors[vendor] || 0) + 1;
    });
    return Object.entries(vendors).map(([vendor, count]) => ({ vendor, count }));
  }, [dashboardData]);

  // Calculate regional breakdown
  const regionData = useMemo(() => {
    const regions = {};
    dashboardData.forEach((site) => {
      const region = site.region || site.pta_province || 'Unknown';
      if (!regions[region]) {
        regions[region] = { active: 0, inactive: 0, maintenance: 0 };
      }
      const status = getStatus(site);
      if (status === 'Active') {
        regions[region].active += 1;
      } else if (status === 'Inactive') {
        regions[region].inactive += 1;
      } else if (status === 'Maintenance') {
        regions[region].maintenance += 1;
      }
    });
    return Object.entries(regions).map(([region, counts]) => ({
      region,
      active: counts.active,
      inactive: counts.inactive,
      maintenance: counts.maintenance,
    }));
  }, [dashboardData]);

  // Calculate technology breakdown
  const technologyData = useMemo(() => {
    const technologies = {};
    dashboardData.forEach((site) => {
      const tech = site.physical || site.lte_technology || 'Unknown';
      technologies[tech] = (technologies[tech] || 0) + 1;
    });
    return Object.entries(technologies).map(([tech, count]) => ({ tech, count }));
  }, [dashboardData]);

  // Calculate coverage type breakdown
  const coverageTypeData = useMemo(() => {
    const coverageTypes = {};
    dashboardData.forEach((site) => {
      const coverage = site.coverage_type || 'Unknown';
      coverageTypes[coverage] = (coverageTypes[coverage] || 0) + 1;
    });
    return Object.entries(coverageTypes).map(([coverage, count]) => ({ coverage, count }));
  }, [dashboardData]);

  // Calculate site type breakdown
  const siteTypeData = useMemo(() => {
    const siteTypes = {};
    dashboardData.forEach((site) => {
      const type = site.site_type || 'Unknown';
      siteTypes[type] = (siteTypes[type] || 0) + 1;
    });
    return Object.entries(siteTypes).map(([type, count]) => ({ type, count }));
  }, [dashboardData]);

  // Status distribution for pie chart
  const statusData = [
    { name: 'Active', value: activeSites, color: '#4CAF50' },
    { name: 'Inactive', value: inactiveSites, color: '#F44336' },
    { name: 'Maintenance', value: maintenanceSites, color: '#FF9800' },
    { name: 'No Status', value: nullStatusSites, color: '#9E9E9E' },
  ];

  const stats = [
    {
      title: 'Total Sites',
      value: totalSites,
      color: '#2196F3',
      bgColor: 'linear-gradient(135deg, #E3F2FD 0%, #BBDEFB 100%)',
      icon: '📊',
      trend: totalSites > 0 ? `+${Math.round((totalSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#4CAF50',
    },
    {
      title: 'Active Sites',
      value: activeSites,
      color: '#4CAF50',
      bgColor: 'linear-gradient(135deg, #E8FAF0 0%, #C8E6C9 100%)',
      icon: '✅',
      percentage: totalSites > 0 ? Math.round((activeSites / totalSites) * 100) : 0,
      trend: totalSites > 0 ? `+${Math.round((activeSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#4CAF50',
    },
    {
      title: 'Inactive Sites',
      value: inactiveSites,
      color: '#F44336',
      bgColor: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
      icon: '❌',
      percentage: totalSites > 0 ? Math.round((inactiveSites / totalSites) * 100) : 0,
      trend: totalSites > 0 ? `+${Math.round((inactiveSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#F44336',
    },
    {
      title: 'Maintenance',
      value: maintenanceSites,
      color: '#FF9800',
      bgColor: 'linear-gradient(135deg, #FFF3E0 0%, #FFE0B2 100%)',
      icon: '🔧',
      percentage: totalSites > 0 ? Math.round((maintenanceSites / totalSites) * 100) : 0,
      trend: totalSites > 0 ? `+${Math.round((maintenanceSites / totalSites) * 100)}%` : '+0%',
      trendColor: '#FF9800',
    },
    {
      title: 'Vendors',
      value: vendorData.length,
      color: '#673AB7',
      bgColor: 'linear-gradient(135deg, #F3E5F5 0%, #E1BEE7 100%)',
      icon: '🏢',
      trend: `+${vendorData.length} vendors`,
      trendColor: '#673AB7',
    },
    {
      title: 'Regions',
      value: regionData.length,
      color: '#FF5722',
      bgColor: 'linear-gradient(135deg, #FFEBEE 0%, #FFCDD2 100%)',
      icon: '🌍',
      trend: `+${regionData.length} regions`,
      trendColor: '#FF5722',
    },
  ];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  // Initialize ECharts
  useEffect(() => {
    const charts = [];

    // Site Status Distribution Chart
    if (siteStatusChartRef.current) {
      const siteStatusChart = echarts.init(siteStatusChartRef.current);
      const siteStatusOption = {
        title: { text: 'Site Status Distribution', left: 'center', bottom: 10 },
        tooltip: { trigger: 'item' },
        legend: { top: '5%', left: 'center' },
        series: [
          {
            name: 'Status Distribution',
            type: 'pie',
            radius: ['40%', '70%'],
            avoidLabelOverlap: false,
            itemStyle: {
              borderRadius: 10,
              borderColor: '#fff',
              borderWidth: 2,
            },
            label: { show: false, position: 'center' },
            emphasis: {
              label: {
                show: true,
                fontSize: 40,
                fontWeight: 'bold',
              },
            },
            labelLine: { show: false },
            data: statusData,
          },
        ],
      };
      siteStatusChart.setOption(siteStatusOption);
      charts.push(siteStatusChart);
    }

    // Vendor Distribution Chart
    if (vendorChartRef.current) {
      const vendorChart = echarts.init(vendorChartRef.current);
      const vendorOption = {
        title: { text: 'Vendor Distribution', left: 'center' },
        tooltip: { trigger: 'item', formatter: '{b}: {c} sites ({d}%)' },
        legend: { orient: 'vertical', left: 'left', top: 'middle' },
        series: [
          {
            name: 'Vendor Sites',
            type: 'pie',
            radius: ['40%', '70%'],
            center: ['60%', '50%'],
            data: vendorData.map((item, index) => ({
              name: item.vendor,
              value: item.count,
              itemStyle: { color: COLORS[index % COLORS.length] },
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
            label: { show: true, formatter: '{b}\n{c} sites', fontSize: 12, fontWeight: 'bold' },
            labelLine: { show: true },
          },
        ],
      };
      vendorChart.setOption(vendorOption);
      charts.push(vendorChart);
    }

    // Regional Distribution Chart
    if (regionChartRef.current) {
      const regionChart = echarts.init(regionChartRef.current);
      const regionOption = {
        title: { text: 'Regional Site Distribution', left: 'center' },
        tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
        legend: { data: ['Active', 'Inactive', 'Maintenance'] },
        xAxis: { type: 'category', data: regionData.map((item) => item.region) },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'Active',
            type: 'bar',
            stack: 'total',
            data: regionData.map((item) => item.active),
            itemStyle: { color: '#4CAF50' },
          },
          {
            name: 'Inactive',
            type: 'bar',
            stack: 'total',
            data: regionData.map((item) => item.inactive),
            itemStyle: { color: '#F44336' },
          },
          {
            name: 'Maintenance',
            type: 'bar',
            stack: 'total',
            data: regionData.map((item) => item.maintenance),
            itemStyle: { color: '#FF9800' },
          },
        ],
      };
      regionChart.setOption(regionOption);
      charts.push(regionChart);
    }

    // Technology Distribution Chart
    if (technologyChartRef.current) {
      const technologyChart = echarts.init(technologyChartRef.current);
      const technologyOption = {
        title: { text: 'Technology Distribution', left: 'center' },
        tooltip: { trigger: 'item' },
        xAxis: { type: 'category', data: technologyData.map((item) => item.tech) },
        yAxis: { type: 'value' },
        series: [
          {
            type: 'bar',
            data: technologyData.map((item, index) => ({
              value: item.count,
              itemStyle: { color: COLORS[index % COLORS.length] },
            })),
            barWidth: '60%',
          },
        ],
      };
      technologyChart.setOption(technologyOption);
      charts.push(technologyChart);
    }

    // Coverage Type Chart
    if (coverageTypeChartRef.current) {
      const coverageTypeChart = echarts.init(coverageTypeChartRef.current);
      const coverageTypeOption = {
        title: { text: 'Coverage Type Distribution', left: 'center' },
        tooltip: { trigger: 'item' },
        series: [
          {
            name: 'Coverage Types',
            type: 'pie',
            radius: '50%',
            data: coverageTypeData.map((item, index) => ({
              name: item.coverage,
              value: item.count,
              itemStyle: { color: COLORS[index % COLORS.length] },
            })),
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.5)',
              },
            },
          },
        ],
      };
      coverageTypeChart.setOption(coverageTypeOption);
      charts.push(coverageTypeChart);
    }

    // Site Type Chart
    if (siteTypeChartRef.current) {
      const siteTypeChart = echarts.init(siteTypeChartRef.current);
      const siteTypeOption = {
        title: { text: 'Site Type Distribution', left: 'center' },
        tooltip: { trigger: 'item' },
        xAxis: { type: 'category', data: siteTypeData.map((item) => item.type) },
        yAxis: { type: 'value' },
        series: [
          {
            type: 'bar',
            data: siteTypeData.map((item, index) => ({
              value: item.count,
              itemStyle: { color: COLORS[index % COLORS.length] },
            })),
            barWidth: '60%',
          },
        ],
      };
      siteTypeChart.setOption(siteTypeOption);
      charts.push(siteTypeChart);
    }

    // Monthly Trends Chart (mock data for demonstration)
    if (monthlyTrendsChartRef.current) {
      const monthlyTrendsChart = echarts.init(monthlyTrendsChartRef.current);
      const monthlyData = [
        { month: 'Jan', active: 120, inactive: 15, maintenance: 5 },
        { month: 'Feb', active: 125, inactive: 12, maintenance: 8 },
        { month: 'Mar', active: 130, inactive: 10, maintenance: 10 },
        { month: 'Apr', active: 135, inactive: 8, maintenance: 12 },
        { month: 'May', active: 140, inactive: 6, maintenance: 14 },
        { month: 'Jun', active: 145, inactive: 5, maintenance: 15 },
      ];
      const monthlyOption = {
        title: { text: 'Monthly Site Trends', left: 'center' },
        tooltip: { trigger: 'axis' },
        legend: { data: ['Active', 'Inactive', 'Maintenance'] },
        xAxis: { type: 'category', data: monthlyData.map((item) => item.month) },
        yAxis: { type: 'value' },
        series: [
          {
            name: 'Active',
            type: 'line',
            data: monthlyData.map((item) => item.active),
            itemStyle: { color: '#4CAF50' },
            smooth: true,
          },
          {
            name: 'Inactive',
            type: 'line',
            data: monthlyData.map((item) => item.inactive),
            itemStyle: { color: '#F44336' },
            smooth: true,
          },
          {
            name: 'Maintenance',
            type: 'line',
            data: monthlyData.map((item) => item.maintenance),
            itemStyle: { color: '#FF9800' },
            smooth: true,
          },
        ],
      };
      monthlyTrendsChart.setOption(monthlyOption);
      charts.push(monthlyTrendsChart);
    }

    // Interactive Chart - Vendor vs Region
    if (interactiveChartRef.current && dashboardData.length > 0) {
      const interactiveChart = echarts.init(interactiveChartRef.current);

      const vendors = [...new Set(dashboardData.map((site) => site.vendor).filter(Boolean))];
      const regions = [...new Set(dashboardData.map((site) => site.region).filter(Boolean))];

      const chartData = [['region', ...vendors]];
      regions.forEach((region) => {
        const row = [region];
        vendors.forEach((vendor) => {
          const count = dashboardData.filter(
            (site) => site.region === region && site.vendor === vendor
          ).length;
          row.push(count);
        });
        chartData.push(row);
      });

      const interactiveOption = {
        title: { text: 'Vendor Distribution by Region', left: 'center' },
        tooltip: { trigger: 'axis' },
        legend: { data: vendors, top: 40 },
        xAxis: { type: 'category', data: regions },
        yAxis: { type: 'value' },
        series: vendors.map((vendor, index) => ({
          name: vendor,
          type: 'bar',
          stack: 'total',
          data: regions.map((region) => {
            const count = dashboardData.filter(
              (site) => site.region === region && site.vendor === vendor
            ).length;
            return count;
          }),
          itemStyle: { color: COLORS[index % COLORS.length] },
        })),
      };

      interactiveChart.setOption(interactiveOption);
      charts.push(interactiveChart);
    }

    // Cleanup function
    return () => {
      charts.forEach((chart) => {
        if (chart && !chart.isDisposed()) {
          chart.dispose();
        }
      });
    };
  }, [
    dashboardData,
    statusData,
    vendorData,
    regionData,
    technologyData,
    coverageTypeData,
    siteTypeData,
  ]);

  // Show loading state
  if (loading || (!isInitialized && dashboardData.length === 0)) {
    return (
      <div style={{ padding: '20px 2%', minHeight: '100vh', position: 'relative' }}>
        <FancyLoader
          message={loading ? 'Loading RanDB dashboard data...' : 'Initializing RanDB dashboard...'}
          size="large"
          fullScreen={true}
        />
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div
        style={{
          padding: '20px 2%',
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            padding: '40px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            maxWidth: '500px',
          }}
        >
          <Typography variant="h5" sx={{ color: '#d32f2f', marginBottom: '16px', fontWeight: 600 }}>
            ⚠️ Something went wrong
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', marginBottom: '24px' }}>
            Unable to load RanDB dashboard data. Please try again later.
          </Typography>
          <button
            onClick={() => fetchDashboardData(true)}
            style={{
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              padding: '12px 24px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            Try Again
          </button>
        </Box>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px 2%', minHeight: '100vh', position: 'relative' }}>
      {/* Background gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)
          `,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      {/* Stats Cards */}
      <Grid
        container
        spacing={2}
        sx={{
          mb: 4,
          position: 'relative',
          zIndex: 1,
          flexWrap: 'nowrap',
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: '6px' },
          '&::-webkit-scrollbar-thumb': { backgroundColor: '#ccc', borderRadius: '4px' },
        }}
      >
        {stats.map((stat, index) => (
          <Grid item key={index} xs>
            <Card
              sx={{
                height: '180px',
                borderRadius: 4,
                background: stat.bgColor,
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.12)',
                transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-8px) scale(1.02)',
                  boxShadow: '0px 16px 48px rgba(0, 0, 0, 0.2)',
                },
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: stat.color,
                  borderRadius: '4px 4px 0 0',
                },
              }}
            >
              <CardContent
                sx={{
                  padding: '16px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    mb: 1,
                  }}
                >
                  <Box sx={{ fontSize: '24px', opacity: 0.8, transition: 'all 0.3s ease' }}>
                    {stat.icon}
                  </Box>
                  {stat.trend && (
                    <Chip
                      label={stat.trend}
                      size="small"
                      sx={{
                        backgroundColor: stat.trendColor + '20',
                        color: stat.trendColor,
                        fontWeight: 600,
                        fontSize: '10px',
                        height: '20px',
                        '& .MuiChip-label': { px: 1 },
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="h3"
                  sx={{
                    color: stat.color,
                    fontWeight: 800,
                    fontSize: '28px',
                    lineHeight: 1,
                    mb: 0.5,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  {stat.value.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#495057',
                    fontWeight: 600,
                    fontSize: '11px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    mb: 2,
                    opacity: 0.8,
                  }}
                >
                  {stat.title}
                </Typography>
                <Box sx={{ flexGrow: 1, position: 'relative' }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -20,
                      right: -20,
                      width: 80,
                      height: 80,
                      borderRadius: '50%',
                      background: `radial-gradient(circle, ${stat.color}15 0%, transparent 70%)`,
                      zIndex: 0,
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Section */}
      <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
        {/* First Row */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={siteStatusChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={vendorChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={regionChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Second Row */}
        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={technologyChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={coverageTypeChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={siteTypeChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Third Row */}
        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={monthlyTrendsChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card
            sx={{
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
              },
            }}
          >
            <CardContent sx={{ overflow: 'hidden' }}>
              <div
                ref={interactiveChartRef}
                style={{ width: '100%', height: '400px', maxWidth: '100%' }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Stats */}
      <Box sx={{ mt: 4, position: 'relative', zIndex: 1 }}>
        <Typography
          variant="h6"
          sx={{ mb: 2, fontWeight: 600, color: '#000000', textShadow: '0 2px 4px rgba(0,0,0,0.3)' }}
        >
          Quick Statistics
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: '#4CAF50', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
              >
                {totalSites > 0 ? ((activeSites / totalSites) * 100).toFixed(1) : 0}%
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#4CAF50',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Active Rate
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: '#2196F3', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
              >
                {vendorData.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#2196F3',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Active Vendors
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: '#FF9800', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
              >
                {regionData.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#FF9800',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Regions Covered
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                boxShadow: '0px 8px 32px rgba(0, 0, 0, 0.1)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0px 12px 40px rgba(0, 0, 0, 0.15)',
                  background: 'rgba(255, 255, 255, 0.15)',
                },
              }}
            >
              <Typography
                variant="h4"
                sx={{ color: '#FF5722', fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.2)' }}
              >
                {technologyData.length}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#FF5722',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                Technologies
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </div>
  );
}
